import { createI18n, type Composer, type LocaleMessageObject } from 'vue-i18n';
import type { Plugin } from 'vue';
import fr from '@shared/translations/fr.json';
import en from '@shared/translations/en.json';
import es from '@shared/translations/es.json';
import it from '@shared/translations/it.json';
import de from '@shared/translations/de.json';
import pt from '@shared/translations/pt.json';
import pl from '@shared/translations/pl.json';
import nl from '@shared/translations/nl.json';
import sv from '@shared/translations/sv.json';
import { messageCompiler, TranslationDependencyTypes } from '@kiplin/web-common/translation';
import { container } from '@shared/common/dependency-injection/domain/container';

// Configuration : Intl.DateTimeFormat options : https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
// Tried to use the format {myDate, date, ::EEEEdMMMM} but no success. https://messageformat.github.io/messageformat/guide/#date-and-time
const dateFormat: Record<string, Intl.DateTimeFormatOptions> = {
  calendarDate: {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  },
  shortCalendarDate: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  },
  dayMonth: {
    day: 'numeric',
    month: 'long',
  },
  announcementTime: {
    hour: '2-digit',
    minute: '2-digit',
  },
  weekdayDayMonth: {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  },
  fulldate: {
    year: 'numeric',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  },
};

export type I18nVuePluginOptions = {
  messages?: Record<string, LocaleMessageObject>;
};

export const I18nVuePlugin: Plugin = {
  install(app, options: I18nVuePluginOptions = {}) {
    // Refacto Vue : Une fois la branche mergée sur develop, supprimer les traductions propre au front (tout sauf error.*) dans le projet loco Cockpit Back.
    const i18n = createI18n({
      legacy: false,
      globalInjection: true,
      locale: 'fr',
      fallbackLocale: 'en',
      missingWarn: process.env.NODE_ENV !== 'test',
      messageCompiler,
      messages: {
        fr,
        en,
        it,
        de,
        es,
        pt,
        pl,
        nl,
        sv,
      },
      datetimeFormats: {
        fr: dateFormat,
        en: dateFormat,
        es: dateFormat,
        it: dateFormat,
        de: dateFormat,
        pt: dateFormat,
        pl: dateFormat,
        nl: dateFormat,
        sv: dateFormat,
      },
      warnHtmlMessage: false,
    });

    for (const [locale, messages] of Object.entries(options.messages ?? {})) {
      i18n.global.mergeLocaleMessage(locale, messages);
    }

    container.bind<Composer>(TranslationDependencyTypes.i18n).toConstantValue(i18n.global as unknown as Composer);
    app.use(i18n);
  },
};
